

import React, { useState, useRef, useLayoutEffect, useContext, useEffect } from "react";
import axios from '../axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import { Menubar } from 'primereact/menubar';
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext';

import { useMountEffect, useUpdateEffect, useIntersectionObserver, useLocalStorage } from 'primereact/hooks';
import moment from 'moment';
import UsersContext from "../Context/UsersContext";
import { classNames } from "primereact/utils";
import { Skeleton } from 'primereact/skeleton';
import AppContext from "../Context/AppContext";
import LayoutContext from "../Context/LayoutContext";

const Logs = () => {
    const [logs, setLogs] = useState([]);
    const [skeleton, setSkeleton] = useState(0);
    const [loading, setLoading] = useState(false);

    const [currentData, setCurrentData] = useState(null);

    const dataParams = useRef({ offset: 0, limit: 16, count: 0, searchText: "" })
    const lastElementRef = useRef(null);
    const lastElementVisible = useIntersectionObserver(lastElementRef);
    const scrollPosition = useRef(0);
    const networkController = useRef(null)
    const scrollRef = useRef(null)

    const { setNavbar } = useContext(LayoutContext)
    const { isDesktop } = useContext(AppContext)

    const [searchText, setSearchText] = useLocalStorage("", "security.logs.search")

    useEffect(() => {
        setNavbar(
            <div className='flex gap-2'>
                <div className="p-inputgroup max-w-20rem flex-1">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-search"></i>
                    </span>
                    <InputText placeholder="Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                </div>
            </div>
        )
    }, [searchText])

    useMountEffect(() => {
        fetchData({ ...dataParams.current }, true);
    });

    useUpdateEffect(() => {
        if (!lastElementVisible)
            return;

        fetchData({ ...dataParams.current }, true)
    }, [lastElementVisible])

    useUpdateEffect(() => {
        dataParams.current.searchText = searchText

        fetchData({ ...dataParams.current, offset: 0 }, skeleton !== 0)
    }, [searchText]);

    const fetchData = async (params, skeleton = false) => {
        try {
            networkController.current?.abort()
            skeleton ? setSkeleton(params.limit) : setLoading(true)

            networkController.current = new AbortController()
            const response = await axios.get('/logs', { signal: networkController.current.signal, params: params });

            const _logs = [...logs.slice(0, params.offset), ...response.data.data]
            setLogs(_logs)

            dataParams.current = { ...dataParams.current, count: response.data.count, offset: params.offset + response.data.data.length }

            setSkeleton(0)
            setLoading(false)
        } catch (error) {
            if (error?.code === 'ERR_CANCELED') {
                console.log("Request Canceled!");
            } else {
                console.error('Error:', error);
            }
        }
    };

    const registrationDateTemplate = (value) => {
        return value ? moment(value.registration_date).format("DD MMMM YYYY hh:mm A") : <Skeleton width="11rem" height="26px" borderRadius="16px"></Skeleton>;
    };

    const dateTemplate = (value) => {
        return value ? moment(value.date).format("DD MMMM YYYY hh:mm A") : <Skeleton width="11rem" height="26px" borderRadius="16px"></Skeleton>;
    };

    const tagTemplate = (value) => {
        return value ? <Tag className='px-2' value={value.tag.toUpperCase()} style={{ color: '#' + value.foreground, backgroundColor: '#' + value.background }} rounded></Tag> : <Skeleton width="4rem" height="26px" borderRadius="16px"></Skeleton>;
    };

    const indexTemplate = (value, props) => {
        return value ? props.rowIndex + 1 : <Skeleton width="2rem" height="26px" borderRadius="16px"></Skeleton>;
    }

    const userTemplate = (value) => {
        return value ? value.user : <Skeleton width="8rem" height="26px" borderRadius="16px"></Skeleton>;
    }

    const codeTemplate = (value) => {
        return value ? value.name : <Skeleton width="8rem" height="26px" borderRadius="16px"></Skeleton>;
    }

    const statusTemplate = (value) => {
        //return value ? <span className={value.status === 1 ? "text-green-500" : value.status === 2 ? "text-red-500" : "text-orange-500"}>{value.status === 1 ? "Success" : value.status === 2 ? "Failure" : "Partial"}</span> : <Skeleton width="8rem" height="26px" borderRadius="16px"></Skeleton>;

        return value ? <div className='flex gap-2 justify-content-end'><i className={classNames("pi text-xl p-1 icon-button", value.status === 1 ? "pi-check" : "pi-times")} style={{ color: value.status === 1 ? 'var(--green-500)' : value.status === 2 ? 'var(--red-500)' : 'var(--yellow-500)'  }} ></i></div> : <Skeleton width="8rem" height="26px" borderRadius="16px"></Skeleton>;
    }
    
    useLayoutEffect(() => {
        scrollRef.current.scrollTo({
            top: scrollPosition.current,
            left: 0,
            behavior: "instant",
        });
    }, [logs]);

    const handleScroll = (event) => {
        scrollPosition.current = event.target.scrollTop
    }

    return (
            <div className="flex flex-column flex-auto surface-section overflow-scroll align-items-center" ref={scrollRef} onScroll={handleScroll}>
                <div className="w-full datacontent flex-1 container relative px-0 md:px-4">
                    <DataTable size={"small"} value={logs.concat(Array(skeleton).fill(null))} className={classNames("users-table surface-ground white-space-nowrap", loading && !skeleton ? "opacity-50" : "")}>
                        <Column field="name" header="" body={indexTemplate} pt={{ bodyCell: { className: 'pl-3 md:pl-5' } }} bodyStyle={{ width: "50px" }}></Column>
                        <Column field="user" header="User" body={userTemplate} bodyStyle={{maxWidth: "0px"}}></Column>
                        <Column field="name" header="Code" body={codeTemplate} bodyStyle={{maxWidth: "0px"}}></Column>
                        <Column field="date" header="Date" body={dateTemplate} bodyStyle={{whiteSpace: "nowrap", maxWidth: "0px", overflow: "hidden", textOverflow: "ellipsis"}}></Column>
                        <Column field="status" header="" body={statusTemplate} bodyStyle={{maxWidth: "0px"}}></Column>
                    </DataTable>
                    {(!loading && skeleton === 0) && <div className={classNames("absolute bottom-0 w-1rem h-20rem", lastElementVisible || dataParams.current.count === dataParams.current.offset ? "hidden" : "")} ref={lastElementRef}></div>}
                </div>
            </div>
    );
}

export default Logs;